import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  ButtonGroup,
  Autocomplete,
  Pagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AccessTime,
  Person,
  Campaign,
  FilterList,
  Download,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown,
} from '@mui/icons-material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { apiClient } from '../services/api';
import { timeRecordDateMillis } from '../utils/timeRecordSort';
import { TimeEntry, User, Campaign as CampaignType } from '../types';
import { TimezoneService } from '../services/timezoneService';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { exportTimeRecordsToCSV, exportTimeRecordsToExcel, exportTimeRecordsToPDF } from '../utils/exportUtils';
import moment from 'moment-timezone';
import { ABSENCE_TYPE_OPTIONS, getAbsenceTypeLabel } from '../constants/absenceReasons';
import { AUTO_NO_SHOW_REASON, isAutoNoShowAbsence } from '../utils/absence';
import { MANUAL_ADJUSTMENT_REASON_OPTIONS } from '../constants/manualTimeAdjustments';

interface TimeEntryFormData {
  userId: string;
  campaignId: string;
  entryType: 'clock_in' | 'clock_out' | 'break_start' | 'break_end' | 'lunch_start' | 'lunch_end' | 'bathroom_start' | 'bathroom_end' | 'overtime_start' | 'overtime_end' | 'absence';
  timestamp: string;
  notes: string;
  adjustmentReason?: string;
  absenceType?: string;
  absenceStartDate?: string;
  absenceEndDate?: string;
  absenceAllDay?: boolean;
  absenceStatus?: string;
}

const TimeRecordsManagementPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canCreateTimeEntries = permissions.includes('time_entries.create');
  const canUpdateTimeEntries = permissions.includes('time_entries.update');
  const canDeleteTimeEntries = permissions.includes('time_entries.delete');
  const canCreateAbsences = permissions.includes('absences.create');
  const canUpdateAbsences = permissions.includes('absences.update');
  const canDeleteAbsences = permissions.includes('absences.delete');
  const location = useLocation();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [absences, setAbsences] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignType[]>([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [timestampDraft, setTimestampDraft] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editingAbsence, setEditingAbsence] = useState<any | null>(null);
  const [formData, setFormData] = useState<TimeEntryFormData>({
    userId: '',
    campaignId: '',
    entryType: 'clock_in',
    timestamp: new Date().toISOString(),
    notes: '',
    adjustmentReason: 'SUPERVISOR_CORRECTION',
    absenceType: '',
    absenceStartDate: '',
    absenceEndDate: '',
    absenceAllDay: true,
    absenceStatus: 'pending',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('SUPERVISOR_CORRECTION');
  const [deleteComment, setDeleteComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    userId: '',
    campaignId: '',
    entryType: '',
    startDate: moment().subtract(2, 'weeks').startOf('day').toDate() as Date | null,
    endDate: moment().endOf('day').toDate() as Date | null,
  });
  const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || TimezoneService.getUserTimezone());
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Format absence date (DATEONLY field) using employee's timezone
  const formatAbsenceDate = (dateValue: string | Date | null | undefined, userId?: string): string => {
    if (!dateValue) return '';
    
    // Get employee's timezone from users array
    const employee = userId ? users.find(u => u.id === userId) : null;
    const employeeTimezone = employee?.timezone || selectedTimezone || user?.timezone || TimezoneService.getUserTimezone() || 'UTC';
    
    // DATEONLY fields are stored as 'YYYY-MM-DD' strings
    // Treat them as dates in the employee's timezone (where the absence was created)
    if (typeof dateValue === 'string') {
      // If it's a DATEONLY string (YYYY-MM-DD), treat it as midnight in the employee's timezone
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const dateInTimezone = moment.tz(dateValue, 'YYYY-MM-DD', employeeTimezone);
        return dateInTimezone.format('MMM D, YYYY');
      }
      // If it includes time, parse and format using employee's timezone
      const datePart = dateValue.substring(0, 10);
      if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const dateInTimezone = moment.tz(datePart, 'YYYY-MM-DD', employeeTimezone);
        return dateInTimezone.format('MMM D, YYYY');
      }
      // Try parsing as UTC datetime and converting to employee's timezone
      try {
        return moment.utc(dateValue).tz(employeeTimezone).format('MMM D, YYYY');
      } catch (e) {
        // Fallback
      }
    }
    
    // If it's a Date object, format using employee's timezone
    if (dateValue instanceof Date) {
      return moment(dateValue).tz(employeeTimezone).format('MMM D, YYYY');
    }
    
    // Fallback: try to parse as date string
    try {
      const dateStr = String(dateValue);
      const datePart = dateStr.substring(0, 10);
      if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const dateInTimezone = moment.tz(datePart, 'YYYY-MM-DD', employeeTimezone);
        return dateInTimezone.format('MMM D, YYYY');
      }
    } catch (e) {
      // Ignore errors
    }
    
    return String(dateValue);
  };
  const [isAutoClockOut, setIsAutoClockOut] = useState(false);
  const [markAsResolved, setMarkAsResolved] = useState(false);
  const [preservedAutoReason, setPreservedAutoReason] = useState<string | null>(null);
  const isProcessingUrlParams = useRef(false);
  const processedUrlSearch = useRef<string>('');
  const dialogJustClosed = useRef(false);

  // Sorting state will be defined after filteredRecords

  // Entry types - absence only available if user has absences.create permission
  const baseEntryTypes = [
    'clock_in',
    'clock_out',
    'break_start',
    'break_end',
    'lunch_start',
    'lunch_end',
    'bathroom_start',
    'bathroom_end',
    'overtime_start',
    'overtime_end'
  ];
  const entryTypes = canCreateAbsences ? [...baseEntryTypes, 'absence'] : baseEntryTypes;

  // Combined record interface for display
  interface CombinedRecord {
    id: string;
    type: 'time_entry' | 'absence';
    userId: string;
    campaignId?: string;
    entryType?: string;
    absenceType?: string;
    timestamp?: string;
    startDate?: string;
    endDate?: string;
    notes?: string;
    reason?: string;
    status?: string;
    createdAt: string;
    updatedAt?: string;
    user?: User;
    campaign?: CampaignType;
  }

  useEffect(() => {
    loadData().then(() => {
      setHasLoaded(true);
    });
    // Reset processed URL search when component mounts
    processedUrlSearch.current = '';
  }, []);

  // Reload data when filters change (but not on initial mount to avoid double load)
  useEffect(() => {
    if (hasLoaded) {
      loadData();
    }
  }, [filters.startDate, filters.endDate, filters.userId, filters.campaignId, filters.entryType]);

  const handleOpenDialog = (entry?: TimeEntry, absence?: any) => {
    if (entry) {
      setEditingEntry(entry);
      setEditingAbsence(null);
      setPreservedAutoReason(null);
      
      // Check if this is an auto clock-out entry
      const isAutoClockOutEntry = !!(entry.notes && 
        (entry.notes.toLowerCase().includes('auto clock-out') || 
         entry.notes.toLowerCase().includes('auto clock')));
      setIsAutoClockOut(isAutoClockOutEntry);

      // Check if already resolved
      const isAlreadyResolved = !!(entry.notes && 
        (entry.notes.includes('CORRECTED') || entry.notes.includes('RESOLVED')));
      setMarkAsResolved(isAlreadyResolved);

      setFormData({
        userId: entry.userId,
        campaignId: entry.campaignId,
        entryType: entry.entryType,
        timestamp: entry.timestamp, // Keep original UTC for API calls
        notes: entry.notes || '',
        adjustmentReason: 'SUPERVISOR_CORRECTION',
        absenceType: '',
        absenceStartDate: '',
        absenceEndDate: '',
        absenceAllDay: true,
        absenceStatus: 'pending',
      });
    } else if (absence) {
      // Only allow absence editing if user has absences.update permission
      if (!canUpdateAbsences) {
        setError('Access denied: You do not have permission to edit absence records');
        return;
      }
      const toDateOnly = (val: any) => {
        if (!val) return '';
        if (typeof val === 'string') return val.substring(0, 10);
        try { return new Date(val).toISOString().substring(0, 10); } catch { return ''; }
      };

      setEditingEntry(null);
      setEditingAbsence(absence);
      setPreservedAutoReason(isAutoNoShowAbsence(absence) ? AUTO_NO_SHOW_REASON : null);
      setFormData({
        userId: absence.userId,
        campaignId: '', // Absences don't have campaigns
        entryType: 'absence',
        timestamp: new Date().toISOString(),
        notes: absence.reason || '',
        adjustmentReason: 'SUPERVISOR_CORRECTION',
        absenceType: absence.absenceType,
        absenceStartDate: toDateOnly(absence.startDate),
        absenceEndDate: toDateOnly(absence.endDate),
        absenceAllDay: toDateOnly(absence.startDate) === toDateOnly(absence.endDate),
        absenceStatus: absence.status || 'pending',
      });
    } else {
      setEditingEntry(null);
      setEditingAbsence(null);
      setPreservedAutoReason(null);
      setFormData({
        userId: '',
        campaignId: '',
        entryType: 'clock_in',
        timestamp: new Date().toISOString(),
        notes: '',
        adjustmentReason: 'SUPERVISOR_CORRECTION',
        absenceType: '',
        absenceStartDate: '',
        absenceEndDate: '',
        absenceAllDay: true,
        absenceStatus: 'pending',
      });
    }
    setTimestampDraft(null);
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  // Handle URL parameters for quick actions from dashboard and missing punches report
  useEffect(() => {
    // Only process URL params after data has loaded (including users for lookup)
    if (loading || users.length === 0) {
      isProcessingUrlParams.current = false;
      return;
    }
    
    // Prevent processing if we're already processing (prevents loops)
    if (isProcessingUrlParams.current) return;
    
    // Don't process if dialog was just closed (prevents reopening after create/update)
    if (dialogJustClosed.current) {
      // Reset flag after a short delay
      setTimeout(() => {
        dialogJustClosed.current = false;
      }, 200);
      isProcessingUrlParams.current = false;
      return;
    }
    
    // Don't process if dialog is already open (prevents reopening after delete/other actions)
    // Check this FIRST - if dialog is open, we shouldn't process anything
    if (dialogOpen) {
      // If there are URL params while dialog is open, mark them as processed
      if (location.search && location.search !== '' && location.search !== '?') {
        processedUrlSearch.current = location.search;
      }
      isProcessingUrlParams.current = false;
      return;
    }
    
    // Prevent processing if we've already handled this exact URL search string
    if (processedUrlSearch.current === location.search) {
      isProcessingUrlParams.current = false;
      return;
    }
    
    // If there are no URL params (empty search), mark as processed and return immediately
    // This prevents reprocessing after canceling or deleting
    if (!location.search || location.search === '' || location.search === '?') {
      // Mark as processed
      processedUrlSearch.current = location.search || '';
      isProcessingUrlParams.current = false;
      return;
    }
    
    // Set flag to prevent concurrent processing
    isProcessingUrlParams.current = true;
    
    const urlParams = new URLSearchParams(location.search);
    const action = urlParams.get('action');
    const editEntryId = urlParams.get('editEntry');
    const dateParam = urlParams.get('date');
    const userIdParam = urlParams.get('userId');
    
    // If no URL params, mark as processed and return
    if (!action && !editEntryId && !dateParam && !userIdParam) {
      processedUrlSearch.current = location.search;
      isProcessingUrlParams.current = false;
      return;
    }
    
    // Handle edit entry from missing punches report or direct link
    if (editEntryId) {
      const isAutoClockOutParam = urlParams.get('isAutoClockOut') === 'true';
      const entryToEdit = timeEntries.find(entry => entry.id === editEntryId);
      if (entryToEdit) {
        // Found the entry, open edit dialog
        handleOpenDialog(entryToEdit);
        // If isAutoClockOut parameter is set, ensure it's marked
        if (isAutoClockOutParam) {
          setIsAutoClockOut(true);
        }
        // Clear URL parameters and mark as processed
        const newPath = location.pathname;
        window.history.replaceState({}, '', newPath);
        processedUrlSearch.current = ''; // Mark as processed with empty search
      } else {
        // Entry not found - might be in a different date range
        console.log('Entry not found in current data. Entry ID:', editEntryId, 'Available entries:', timeEntries.length);
        // If date parameter is provided, try to load data for that date
        if (dateParam) {
          try {
            const dateObj = new Date(dateParam);
            if (!isNaN(dateObj.getTime())) {
              setFilters(prev => ({
                ...prev,
                startDate: dateObj,
                endDate: dateObj
              }));
            }
          } catch (error) {
            console.error('Error parsing date parameter:', error);
          }
        }
        // Mark as processed even if entry not found
        processedUrlSearch.current = location.search;
        isProcessingUrlParams.current = false;
      }
    } else if (!action && (dateParam || userIdParam)) {
      if (dateParam) {
        try {
          const dateObj = new Date(dateParam);
          if (!isNaN(dateObj.getTime())) {
            setFilters(prev => ({
              ...prev,
              startDate: dateObj,
              endDate: dateObj
            }));
          }
        } catch (error) {
          console.error('Error parsing date parameter:', error);
        }
      }
      if (userIdParam) {
        setFilters(prev => ({
          ...prev,
          userId: userIdParam
        }));
      }
      processedUrlSearch.current = location.search;
      isProcessingUrlParams.current = false;
      return;
    } else if (action === 'add-entry') {
      // Open dialog for adding time entry
      // Check if we have pre-filled data from missing punches report
      const employeeId = urlParams.get('employeeId');
      const employeeEmail = urlParams.get('employeeEmail');
      const campaignId = urlParams.get('campaignId') || '';
      const entryType = (urlParams.get('entryType') as any) || 'clock_in';
      const startEntryTimestamp = urlParams.get('startEntryTimestamp');
      
      // Look up user by employeeId or email
      let userId = '';
      if (employeeId || employeeEmail) {
        const foundUser = users.find(u => 
          (employeeId && (u as any).employeeId === employeeId) ||
          (employeeEmail && u.email === employeeEmail)
        );
        if (foundUser) {
          userId = foundUser.id;
        }
      }
      
      // Calculate timestamp: for missing pairs, set it to a reasonable time after start entry
      let timestamp = new Date().toISOString();
      if (startEntryTimestamp) {
        const startTime = new Date(startEntryTimestamp);
        // For missing pairs, set timestamp to current time or start time + 1 hour (whichever is later)
        const minTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour after start
        timestamp = new Date(Math.max(minTime.getTime(), Date.now())).toISOString();
      }
      
      setFormData({
        userId,
        campaignId,
        entryType,
        timestamp,
        notes: '',
        adjustmentReason: 'SUPERVISOR_CORRECTION',
        absenceType: '',
        absenceStartDate: '',
        absenceEndDate: '',
        absenceAllDay: true,
        absenceStatus: 'pending',
      });
      setDialogOpen(true);
      setEditingEntry(null);
      setEditingAbsence(null);
      // Clear URL parameters and mark as processed
      const newPath = location.pathname;
      window.history.replaceState({}, '', newPath);
      processedUrlSearch.current = ''; // Mark as processed with empty search
    } else if (action === 'add-absence') {
      // Only allow absence creation if user has absences.create permission
      if (!canCreateAbsences) {
        setError('Access denied: You do not have permission to create absence records');
        processedUrlSearch.current = location.search;
        isProcessingUrlParams.current = false;
        return;
      }
      // Open dialog for adding absence
      setFormData({
        userId: '',
        campaignId: '',
        entryType: 'absence',
        timestamp: new Date().toISOString(),
        notes: '',
        adjustmentReason: 'SUPERVISOR_CORRECTION',
        absenceType: '',
        absenceStartDate: '',
        absenceEndDate: '',
        absenceAllDay: true,
        absenceStatus: 'pending',
      });
      setDialogOpen(true);
      setEditingEntry(null);
      setEditingAbsence(null);
      const newPath = location.pathname;
      window.history.replaceState({}, '', newPath);
      processedUrlSearch.current = ''; // Mark as processed with empty search
    }
    
    // If date parameter is provided (and not already handled above), set the date filter
    if (dateParam && !editEntryId && !action) {
      try {
        const dateObj = new Date(dateParam);
        if (!isNaN(dateObj.getTime())) {
          setFilters(prev => ({
            ...prev,
            startDate: dateObj,
            endDate: dateObj
          }));
        }
        processedUrlSearch.current = location.search;
        isProcessingUrlParams.current = false;
      } catch (error) {
        console.error('Error parsing date parameter:', error);
        processedUrlSearch.current = location.search;
        isProcessingUrlParams.current = false;
      }
    }
    
    // Reset processing flag at the end
    isProcessingUrlParams.current = false;
  }, [location.search, loading, users, dialogOpen]);

  // Combine time entries and absences into a unified list (memoized for performance)
  const getCombinedRecords = useMemo((): CombinedRecord[] => {
    const combined: CombinedRecord[] = [];

    // Add time entries
    timeEntries.forEach(entry => {
      combined.push({
        id: entry.id,
        type: 'time_entry',
        userId: entry.userId,
        campaignId: entry.campaignId,
        entryType: entry.entryType,
        timestamp: entry.timestamp,
        notes: entry.notes,
        createdAt: entry.createdAt,
        updatedAt: (entry as any).updatedAt || entry.createdAt,
        user: entry.user,
        campaign: entry.campaign
      });
    });

    // Add absences
    absences.forEach(absence => {
      combined.push({
        id: absence.id,
        type: 'absence',
        userId: absence.userId,
        absenceType: absence.absenceType,
        startDate: absence.startDate,
        endDate: absence.endDate,
        reason: absence.reason,
        status: absence.status,
        createdAt: absence.createdAt,
        updatedAt: absence.updatedAt || absence.createdAt,
        user: absence.user
      });
    });

    // Sort by creation date (newest first)
    return combined.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [timeEntries, absences]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Build query parameters for filtering
      const hasFilters = filters.userId || filters.campaignId || filters.entryType || filters.startDate || filters.endDate;
      
      let timeEntriesData;
      if (hasFilters) {
        // Use getReport when filters are applied
        const params: any = {};
        if (filters.userId) params.userId = filters.userId;
        if (filters.campaignId) params.campaignId = filters.campaignId;
        if (filters.entryType) params.entryType = filters.entryType;
        if (filters.startDate) {
          params.startDate = formatFilterDate(filters.startDate);
        }
        if (filters.endDate) {
          params.endDate = formatFilterDate(filters.endDate);
        }
        params.page = 1;
        params.limit = 5000; // Reasonable limit for management page (reduced from 10000 for better performance)
        timeEntriesData = await apiClient.timeEntries.getReport(params);
      } else {
        // Use getAll when no filters, but with a reasonable limit
        // Don't load everything - use a date range default (last 2 weeks)
        const params: any = {
          startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
          page: 1,
          limit: 5000 // Reasonable limit
        };
        timeEntriesData = await apiClient.timeEntries.getReport(params);
      }

      const absenceParams = new URLSearchParams();
      if (filters.userId) {
        absenceParams.append('userId', filters.userId);
      }
      if (filters.startDate) {
        absenceParams.append('startDate', formatFilterDate(filters.startDate));
      }
      if (filters.endDate) {
        absenceParams.append('endDate', formatFilterDate(filters.endDate));
      }
      absenceParams.append('page', '1');
      absenceParams.append('limit', '5000');

      const [absencesData, usersData, campaignsData] = await Promise.all([
        apiClient.absences.getAll(absenceParams.toString()),
        apiClient.users.getAll(),
        apiClient.campaigns.getAll({
          includeWithActivityFrom: filters.startDate ? formatFilterDate(filters.startDate) : undefined,
          includeWithActivityTo: filters.endDate ? formatFilterDate(filters.endDate) : undefined,
        }),
      ]);
      
      setTimeEntries(timeEntriesData.data.data || []);
      setAbsences(absencesData.data.data || []);
      setUsers(usersData.data.data || []);
      setCampaigns(campaignsData.data.data || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load data';
      setError(`Failed to load data: ${errorMessage}`);
      // Set empty arrays to prevent undefined errors
      setTimeEntries([]);
      setAbsences([]);
      setUsers([]);
      setCampaigns([]);
    } finally {
      setLoading(false);
      setBootstrapped(true);
    }
  };

  const handleCloseDialog = () => {
    setTimestampDraft(null);
    // Set dialog to closed FIRST to prevent useEffect from processing
    setDialogOpen(false);
    
    // Mark that dialog was just closed to prevent reopening
    dialogJustClosed.current = true;
    
    // Clear URL parameters to prevent reopening
    const currentSearch = location.search;
    if (currentSearch && currentSearch !== '' && currentSearch !== '?') {
      // Mark current search as processed before clearing
      processedUrlSearch.current = currentSearch;
      window.history.replaceState({}, '', location.pathname);
      // Mark empty search as processed
      processedUrlSearch.current = '';
    } else {
      // If already empty, just mark as processed
      processedUrlSearch.current = '';
    }
    
    // Clear form state
    setEditingEntry(null);
    setEditingAbsence(null);
    setError(null);
    setSuccess(null);
    setSubmitting(false);
    setIsAutoClockOut(false);
    setMarkAsResolved(false);
    setPreservedAutoReason(null);
  };

  const noteIsOnlyAutomatic = (notes: string) => (notes || '').trim() === AUTO_NO_SHOW_REASON;

  const changeForm = (patch: Partial<TimeEntryFormData> | ((prev: TimeEntryFormData) => Partial<TimeEntryFormData>)) => {
    setFormData(prev => {
      const resolved = typeof patch === 'function' ? patch(prev) : patch;
      const next = { ...prev, ...resolved };
      if (preservedAutoReason && noteIsOnlyAutomatic(prev.notes) && resolved.notes === undefined) {
        next.notes = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) {
      return;
    }

    const trimmedNotes = (formData.notes || '').trim();
    if (!trimmedNotes || (preservedAutoReason && trimmedNotes === preservedAutoReason)) {
      setError(
        formData.entryType === 'absence'
          ? (preservedAutoReason
            ? 'Enter a reason for this change. The automatic no-show note is added when you save.'
            : 'Reason is required when creating or editing an absence record')
          : 'Notes are required when creating or editing a time entry',
      );
      return;
    }

    if (formData.entryType !== 'absence') {
      const reason = (formData.adjustmentReason || '').trim();
      if (!reason) {
        setError('Adjustment reason is required for manual time record changes');
        return;
      }
      if (reason === 'OTHER' && !trimmedNotes) {
        setError('Comment is required when reason is Other');
        return;
      }
    }
    
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (formData.entryType === 'absence') {
        // Only allow absence creation/update if user has absences.create or absences.update permission
        if (editingAbsence && !canUpdateAbsences) {
          setError('Access denied: You do not have permission to update absence records');
          setSubmitting(false);
          return;
        }
        if (!editingAbsence && !canCreateAbsences) {
          setError('Access denied: You do not have permission to create absence records');
          setSubmitting(false);
          return;
        }
        
        // Handle absence record creation
        if (!formData.absenceType || !formData.absenceStartDate) {
          setError('Absence type and start date are required for absence records');
          setSubmitting(false);
          return;
        }

        // For all day absences, use the same date for start and end
        // For date range absences, use the provided end date
        const endDate = formData.absenceAllDay ? formData.absenceStartDate : formData.absenceEndDate;
        
        if (!endDate) {
          setError('End date is required for date range absences');
          setSubmitting(false);
          return;
        }

        const reasonToSave = preservedAutoReason && !trimmedNotes.includes(preservedAutoReason)
          ? `${trimmedNotes}\n\n${preservedAutoReason}`
          : trimmedNotes;

        const absenceData = {
          userId: formData.userId,
          absenceType: formData.absenceType,
          startDate: formData.absenceStartDate,
          endDate: endDate,
          reason: reasonToSave,
          status: formData.absenceStatus || 'pending'
        };

        if (editingAbsence) {
          await apiClient.absences.update(editingAbsence.id, absenceData);
          setSuccess('Absence record updated successfully');
        } else {
          await apiClient.absences.create(absenceData);
          setSuccess('Absence record created successfully');
        }
      } else {
        // Handle regular time entry
        let updateData = {
          ...formData,
          notes: trimmedNotes,
          adjustmentReason: formData.adjustmentReason || 'SUPERVISOR_CORRECTION',
          adjustmentComment: trimmedNotes,
        };
        
        // If editing auto clock-out and markAsResolved is checked, add CORRECTED to notes
        if (editingEntry && isAutoClockOut && markAsResolved) {
          const existingNotes = updateData.notes || '';
          const userFullName = user ? `${user.firstName} ${user.lastName}` : 'Supervisor';
          const correctedNote = `CORRECTED by ${userFullName} at ${new Date().toISOString()}`;
          
          // Check if already has CORRECTED or RESOLVED
          if (!existingNotes.includes('CORRECTED') && !existingNotes.includes('RESOLVED')) {
            updateData.notes = existingNotes 
              ? `${existingNotes}\n\nRESOLVED: ${correctedNote}`
              : `RESOLVED: ${correctedNote}`;
            updateData.adjustmentComment = updateData.notes;
          }
        }
        
        if (editingEntry) {
          await apiClient.timeEntries.update(editingEntry.id, updateData);
          setSuccess('Time entry updated successfully');
        } else {
          await apiClient.timeEntries.create(updateData);
          setSuccess('Time entry created successfully');
        }
      }

      // Close dialog immediately and clear URL params before reloading data
      setDialogOpen(false);
      
      // Mark that dialog was just closed to prevent reopening
      dialogJustClosed.current = true;
      
      // Clear form state
      setEditingEntry(null);
      setEditingAbsence(null);
      setError(null);
      setSuccess(null);
      setSubmitting(false);
      setIsAutoClockOut(false);
      setMarkAsResolved(false);
      
      // Clear URL parameters to prevent reopening
      const currentSearch = location.search;
      if (currentSearch && currentSearch !== '' && currentSearch !== '?') {
        processedUrlSearch.current = currentSearch;
        window.history.replaceState({}, '', location.pathname);
        processedUrlSearch.current = '';
      } else {
        processedUrlSearch.current = '';
      }
      
      // Reload data in background (don't await to ensure dialog closes immediately)
      loadData().catch((err) => {
        console.error('Error reloading data after save:', err);
        // Don't show error to user since save was successful
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save record');
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (entryId: string) => {
    setPendingDeleteId(entryId);
    setDeleteReason('SUPERVISOR_CORRECTION');
    setDeleteComment('');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const comment = deleteComment.trim();
    if (!deleteReason) {
      setError('Adjustment reason is required to delete a time record');
      return;
    }
    if (!comment) {
      setError('A meaningful comment is required when deleting a time record');
      return;
    }
    try {
      if (dialogOpen) {
        setDialogOpen(false);
        dialogJustClosed.current = true;
        processedUrlSearch.current = location.search || '';
        window.history.replaceState({}, '', location.pathname);
        processedUrlSearch.current = '';
      }
      setDeleteDialogOpen(false);
      await apiClient.timeEntries.delete(pendingDeleteId, {
        adjustmentReason: deleteReason,
        adjustmentComment: comment,
        notes: comment,
      });
      setPendingDeleteId(null);
      setDeleteComment('');
      setSuccess('Time entry deleted successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete time entry');
    }
  };

  const handleDelete = (entryId: string) => {
    openDeleteDialog(entryId);
  };

  const handleDeleteAbsence = async (absenceId: string) => {
    if (window.confirm('Are you sure you want to delete this absence record?')) {
      try {
        // Close dialog if it's open
        if (dialogOpen) {
          setDialogOpen(false);
          dialogJustClosed.current = true;
          processedUrlSearch.current = location.search || '';
          window.history.replaceState({}, '', location.pathname);
          processedUrlSearch.current = '';
        }
        
        await apiClient.absences.delete(absenceId);
        setSuccess('Absence record deleted successfully');
        await loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete absence record');
      }
    }
  };

  // Memoize these functions to prevent unnecessary recalculations
  const getUserName = useCallback((userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  }, [users]);

  const getCampaignName = useCallback((campaignId: string) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || 'Unknown';
  }, [campaigns]);

  const getEntryTypeColor = (entryType: string) => {
    const colors: { [key: string]: string } = {
      clock_in: 'success',
      clock_out: 'error',
      break_start: 'warning',
      break_end: 'info',
      lunch_start: 'secondary',
      lunch_end: 'primary',
      bathroom_start: 'warning',
      bathroom_end: 'info',
      overtime_start: 'error',
      overtime_end: 'success',
      absence: 'warning',
    };
    return colors[entryType] || 'default';
  };

  const getAbsenceTypeColor = (type: string) => {
    switch (type) {
      case 'vacation': return 'success';
      case 'sick': return 'error';
      case 'personal': return 'info';
      case 'other': return 'default';
      case 'unexcused': return 'error';
      case 'holiday': return 'warning';
      case 'bereavement': return 'secondary';
      case 'maternity': return 'primary';
      case 'jury_duty': return 'info';
      case 'no_show': return 'error';
      case 'training': return 'success';
      case 'suspension': return 'error';
      default: return 'default';
    }
  };

  const formatFilterDate = (value: Date) => moment.utc(value).format('YYYY-MM-DD');

  // Memoize filtered records to avoid recalculating on every render (especially when typing in notes field)
  const filteredRecords = useMemo(() => {
    const formatDateOnly = (value: Date) => formatFilterDate(value);
    const normalizeAbsenceDate = (value: string | Date) => {
      if (typeof value === 'string') {
        const datePart = value.substring(0, 10);
        return datePart.match(/^\d{4}-\d{2}-\d{2}$/) ? datePart : moment.utc(value).format('YYYY-MM-DD');
      }
      return moment.utc(value).format('YYYY-MM-DD');
    };
    const parseDateOnly = (value: string | Date, timezone: string) => {
      if (typeof value === 'string') {
        if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return moment.tz(value, 'YYYY-MM-DD', timezone);
        }
        return moment(value).tz(timezone);
      }
      return moment(value).tz(timezone);
    };

    const filterStartDateStr = filters.startDate ? formatDateOnly(filters.startDate) : null;
    const filterEndDateStr = filters.endDate ? formatDateOnly(filters.endDate) : null;
    const filterStart = filterStartDateStr
      ? moment.tz(filterStartDateStr, 'YYYY-MM-DD', selectedTimezone).startOf('day')
      : null;
    const filterEnd = filterEndDateStr
      ? moment.tz(filterEndDateStr, 'YYYY-MM-DD', selectedTimezone).endOf('day')
      : null;

    return getCombinedRecords.filter(record => {
      if (filters.userId && record.userId !== filters.userId) return false;
      if (filters.campaignId && record.campaignId && record.campaignId !== filters.campaignId) return false;
      if (filters.entryType && record.entryType && record.entryType !== filters.entryType) return false;

      // Date filtering - handle both time entries and absences
      if (record.type === 'time_entry') {
        const recordMoment = moment(record.timestamp || record.createdAt).tz(selectedTimezone);
        if (filterStart && recordMoment.isBefore(filterStart)) return false;
        if (filterEnd && recordMoment.isAfter(filterEnd)) return false;
      } else {
        const absenceStartStr = record.startDate
          ? normalizeAbsenceDate(record.startDate)
          : normalizeAbsenceDate(record.createdAt);
        const absenceEndStr = record.endDate
          ? normalizeAbsenceDate(record.endDate)
          : absenceStartStr;
        if (filterStartDateStr && absenceEndStr < filterStartDateStr) return false;
        if (filterEndDateStr && absenceStartStr > filterEndDateStr) return false;
      }

      return true;
    });
  }, [getCombinedRecords, filters.userId, filters.campaignId, filters.entryType, filters.startDate, filters.endDate, selectedTimezone]);

  // Sorting state for table (default newest first by date/period)
  type SortCol = 'type' | 'employee' | 'campaign' | 'kind' | 'date' | 'status' | 'lastMod';
  const [sortBy, setSortBy] = useState<SortCol>('lastMod');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const sortedRecords: CombinedRecord[] = useMemo(() => {
    const list = [...filteredRecords];
    list.sort((a: CombinedRecord, b: CombinedRecord) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'employee':
          av = (getUserName(a.userId) || '').toLowerCase();
          bv = (getUserName(b.userId) || '').toLowerCase();
          break;
        case 'campaign':
          av = (a.campaignId ? getCampaignName(a.campaignId) : '').toLowerCase();
          bv = (b.campaignId ? getCampaignName(b.campaignId) : '').toLowerCase();
          break;
        case 'kind':
          av = (a.type === 'time_entry' ? a.entryType : a.absenceType) || '';
          bv = (b.type === 'time_entry' ? b.entryType : b.absenceType) || '';
          break;
        case 'status':
          av = a.status || '';
          bv = b.status || '';
          break;
        case 'type':
          av = a.type;
          bv = b.type;
          break;
        case 'lastMod':
          av = new Date(a.updatedAt || a.createdAt || 0).getTime();
          bv = new Date(b.updatedAt || b.createdAt || 0).getTime();
          break;
        case 'date':
        default:
          av = timeRecordDateMillis(a, selectedTimezone);
          bv = timeRecordDateMillis(b, selectedTimezone);
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [filteredRecords, sortBy, sortDir, getUserName, getCampaignName, selectedTimezone]);

  // Paginate sorted records - only render what's needed
  const paginatedRecords = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedRecords.slice(startIndex, endIndex);
  }, [sortedRecords, page, rowsPerPage]);

  // Memoize Autocomplete value to prevent expensive find() on every render
  const selectedUser = useMemo(() => {
    if (!formData.userId || users.length === 0) return null;
    return users.find(u => u.id === formData.userId) || null;
  }, [users, formData.userId]);

  const handleHeaderClick = (col: SortCol) => {
    if (sortBy === col) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'date' ? 'desc' : 'asc');
    }
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCSV = () => {
    exportTimeRecordsToCSV(sortedRecords, selectedTimezone);
    handleExportMenuClose();
  };

  const handleExportExcel = () => {
    exportTimeRecordsToExcel(sortedRecords, selectedTimezone);
    handleExportMenuClose();
  };

  const handleExportPDF = () => {
    exportTimeRecordsToPDF(sortedRecords, selectedTimezone);
    handleExportMenuClose();
  };

  if (!bootstrapped && loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
              Time Records & Absences Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage time entries and absence records for all employees. Past dates are allowed for corrections and missing entries.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ButtonGroup variant="outlined">
              <Button
                startIcon={<Download />}
                onClick={handleExportMenuOpen}
                endIcon={<ArrowDropDown />}
              >
                Export
              </Button>
            </ButtonGroup>
            
            {/* Export Menu */}
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <MenuItem onClick={handleExportCSV}>
                <ListItemIcon>
                  <CsvIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export to CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportExcel}>
                <ListItemIcon>
                  <ExcelIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export to Excel</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportPDF}>
                <ListItemIcon>
                  <PdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export to PDF</ListItemText>
              </MenuItem>
            </Menu>
            
            {/* Timezone Switcher - Admin Only */}
            {['administrator', 'super_user'].includes(user?.role || '') && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  label="Timezone"
                >
                  {TimezoneService.getCommonTimezones().map((tz) => (
                    <MenuItem key={tz.timezone} value={tz.timezone}>
                      {tz.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {canCreateTimeEntries && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Add Entry
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  size="small"
                  options={users}
                  value={filters.userId ? users.find(u => u.id === filters.userId) || null : null}
                  onChange={(event, newValue) => {
                    setFilters({ ...filters, userId: newValue?.id || '' });
                  }}
                  getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName}` : ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Employee" placeholder="Search employee..." />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.firstName} {option.lastName}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No employees found"
                  clearOnEscape
                  selectOnFocus
                  handleHomeEndKeys
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const searchLower = inputValue.toLowerCase();
                    return options.filter(option =>
                      `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                      (option.employeeId && option.employeeId.toLowerCase().includes(searchLower))
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={filters.campaignId}
                    onChange={(e) => setFilters({ ...filters, campaignId: e.target.value })}
                    label="Campaign"
                  >
                    <MenuItem value="">All Campaigns</MenuItem>
                    {campaigns.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Entry Type</InputLabel>
                  <Select
                    value={filters.entryType}
                    onChange={(e) => setFilters({ ...filters, entryType: e.target.value })}
                    label="Entry Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {entryTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.replace('_', ' ').toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={filters.startDate ? formatFilterDate(filters.startDate) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
                    setFilters((prev) => ({ ...prev, startDate: value ? new Date(`${value}T00:00:00Z`) : null }));
                  }}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="End Date"
                  type="date"
                  value={filters.endDate ? formatFilterDate(filters.endDate) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
                    setFilters((prev) => ({ ...prev, endDate: value ? new Date(`${value}T00:00:00Z`) : null }));
                  }}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  onClick={() => setFilters({
                    userId: '',
                    campaignId: '',
                    entryType: '',
                    startDate: moment().subtract(2, 'weeks').startOf('day').toDate(),
                    endDate: moment().endOf('day').toDate(),
                  })}
                  fullWidth
                >
                  Reset to 2 Weeks
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('type')}>Type {sortBy==='type'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('employee')}>Employee {sortBy==='employee'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('campaign')}>Campaign {sortBy==='campaign'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('kind')}>Entry/Absence Type {sortBy==='kind'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('date')}>Date/Period {sortBy==='date'?(sortDir==='asc'?'▲':'▼'):''}
                      {['administrator', 'super_user'].includes(user?.role || '') && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          ({TimezoneService.getTimezoneDisplayName(selectedTimezone)})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>Notes/Reason</TableCell>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('lastMod')}>Last Modified {sortBy==='lastMod'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleHeaderClick('status')}>Status {sortBy==='status'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRecords.map((record: CombinedRecord) => (
                    <TableRow key={`${record.type}-${record.id}`}>
                          <TableCell>
                            <Chip
                              label={record.type === 'time_entry' ? 'Time Entry' : 'Absence'}
                              color={record.type === 'time_entry' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Person sx={{ mr: 1, fontSize: 16 }} />
                              {getUserName(record.userId)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Campaign sx={{ mr: 1, fontSize: 16 }} />
                              {record.campaignId ? getCampaignName(record.campaignId) : 'N/A'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                record.type === 'time_entry' 
                                  ? record.entryType!.replace('_', ' ').toUpperCase()
                                  : getAbsenceTypeLabel(record.absenceType!, record.reason)
                              }
                              color={
                                record.type === 'time_entry' 
                                  ? getEntryTypeColor(record.entryType!) as any
                                  : getAbsenceTypeColor(record.absenceType!) as any
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <AccessTime sx={{ mr: 1, fontSize: 16 }} />
                              {record.type === 'time_entry' 
                                ? TimezoneService.formatDateTimeConsistent(record.timestamp!, selectedTimezone)
                                : `${formatAbsenceDate(record.startDate!, record.userId)} - ${formatAbsenceDate(record.endDate!, record.userId)}`
                              }
                            </Box>
                          </TableCell>
                          <TableCell>
                            {record.type === 'time_entry' 
                              ? (record.notes || 'No notes')
                              : (record.reason || 'No reason')
                            }
                          </TableCell>
                          <TableCell>
                            {TimezoneService.formatDateTimeConsistent(record.updatedAt || record.createdAt, selectedTimezone)}
                          </TableCell>
                          <TableCell>
                            {record.type === 'absence' && record.status && (
                              <Chip
                                label={record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                                color={
                                  record.status === 'approved' ? 'success' :
                                  record.status === 'rejected' ? 'error' : 'warning'
                                }
                                size="small"
                              />
                            )}
                            {record.type === 'time_entry' && 'N/A'}
                          </TableCell>
                          <TableCell>
                          {record.type === 'time_entry' && (canUpdateTimeEntries || canDeleteTimeEntries) && (
                              <>
                                {canUpdateTimeEntries && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(record as any)}
                                    color="primary"
                                  >
                                    <Edit />
                                  </IconButton>
                                )}
                                {canDeleteTimeEntries && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(record.id)}
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                )}
                              </>
                            )}
                            {record.type === 'absence' && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(undefined, record)}
                                  color="primary"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAbsence(record.id)}
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {((page - 1) * rowsPerPage) + 1} - {Math.min(page * rowsPerPage, sortedRecords.length)} of {sortedRecords.length} records
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Per Page</InputLabel>
                      <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setPage(1);
                        }}
                        label="Per Page"
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </FormControl>
                    <Pagination
                      count={Math.ceil(sortedRecords.length / rowsPerPage)}
                      page={page}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
            
          </CardContent>
        </Card>

        {/* Add/Edit Time Entry Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown={false}
          onBackdropClick={handleCloseDialog}
        >
            <DialogTitle>
              {editingEntry ? 'Edit Time Entry' : editingAbsence ? 'Edit Absence Record' : 'Add New Entry'}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 'normal' }}>
                {formData.entryType === 'absence' 
                  ? 'Create or edit absence records for employees who are scheduled but have no time entries'
                  : 'You can select past dates to correct missing entries or update existing records'
                }
              </Typography>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  {error && (
                    <Alert severity="error" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  <Autocomplete
                    options={users}
                    value={selectedUser}
                    onChange={(event, newValue) => {
                      changeForm({ userId: newValue?.id || '' });
                    }}
                    getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName} (${option.employeeId})` : ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee"
                        required
                        placeholder="Search employee..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.firstName} {option.lastName} ({option.employeeId})
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No employees found"
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    disableListWrap
                    ListboxProps={{
                      style: { maxHeight: '300px' } // Limit visible options
                    }}
                    filterOptions={(options, { inputValue }) => {
                      // Only show options when user types (lazy loading)
                      if (!inputValue || inputValue.length < 1) {
                        // Show only first 50 when no input to speed up initial render
                        return options.slice(0, 50);
                      }
                      const searchLower = inputValue.toLowerCase();
                      return options.filter(option =>
                        `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                        (option.employeeId && option.employeeId.toLowerCase().includes(searchLower))
                      ).slice(0, 100); // Limit filtered results too
                    }}
                  />
                <FormControl fullWidth required={formData.entryType !== 'absence'}>
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={formData.campaignId}
                    onChange={(e) => changeForm({ campaignId: e.target.value })}
                    label="Campaign"
                    disabled={formData.entryType === 'absence'}
                  >
                    {campaigns.slice(0, 100).map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Entry Type</InputLabel>
                  <Select
                    value={formData.entryType}
                    onChange={(e) => changeForm({ entryType: e.target.value as TimeEntryFormData['entryType'] })}
                    label="Entry Type"
                  >
                    {entryTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.replace('_', ' ').toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Absence Type Dropdown - Only show when Entry Type is 'absence' */}
                {formData.entryType === 'absence' && (
                  <FormControl fullWidth required>
                    <InputLabel>Absence Type</InputLabel>
                    <Select
                      value={formData.absenceType || ''}
                      onChange={(e) => changeForm({ absenceType: e.target.value })}
                      label="Absence Type"
                    >
                      {ABSENCE_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                {/* Absence Date Fields - Only show when Entry Type is 'absence' */}
                {formData.entryType === 'absence' && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.absenceAllDay || false}
                          onChange={(e) => {
                            const allDay = e.target.checked;
                            changeForm(prev => ({
                              absenceAllDay: allDay,
                              absenceEndDate: allDay ? '' : prev.absenceEndDate,
                            }));
                          }}
                        />
                      }
                      label="All Day"
                    />
                    
                    <TextField
                      label={formData.absenceAllDay ? "Date" : "Start Date"}
                      type="date"
                      value={formData.absenceStartDate || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
                        changeForm({ absenceStartDate: value });
                      }}
                      required
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    
                    {!formData.absenceAllDay && (
                      <TextField
                        label="End Date"
                        type="date"
                        value={formData.absenceEndDate || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
                          changeForm({ absenceEndDate: value });
                        }}
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </>
                )}
                
                {/* Absence Status Dropdown - Only show when Entry Type is 'absence' */}
                {formData.entryType === 'absence' && (
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.absenceStatus || 'pending'}
                      onChange={(e) => changeForm({ absenceStatus: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                )}
                
                {/* Timestamp Field - Only show when Entry Type is NOT 'absence' */}
                {formData.entryType !== 'absence' && (
                  <MuiTextField
                    label="Timestamp"
                    type="datetime-local"
                    value={timestampDraft ?? TimezoneService.formatForDateTimeLocal(formData.timestamp, selectedTimezone)}
                    onChange={(e) => {
                      const localDateTime = e.target.value;
                      setTimestampDraft(localDateTime);
                      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(localDateTime)) return;
                      const utcDateTime = TimezoneService.convertDateTimeLocalToUTC(localDateTime, selectedTimezone);
                      if (!utcDateTime || Number.isNaN(new Date(utcDateTime).getTime())) return;
                      setFormData(prev => ({ ...prev, timestamp: utcDateTime }));
                    }}
                    onBlur={() => setTimestampDraft(null)}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={`Past dates are allowed for corrections and missing entries (${selectedTimezone})`}
                  />
                )}
                {formData.entryType !== 'absence' && (
                  <FormControl fullWidth required>
                    <InputLabel>Adjustment Reason</InputLabel>
                    <Select
                      value={formData.adjustmentReason || 'SUPERVISOR_CORRECTION'}
                      onChange={(e) => setFormData(prev => ({ ...prev, adjustmentReason: e.target.value }))}
                      label="Adjustment Reason"
                    >
                      {MANUAL_ADJUSTMENT_REASON_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label={formData.entryType === 'absence' ? 'Reason' : 'Notes / Comment'}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  onFocus={() => {
                    if (preservedAutoReason && noteIsOnlyAutomatic(formData.notes)) {
                      setFormData(prev => ({ ...prev, notes: '' }));
                    }
                  }}
                  multiline
                  rows={3}
                  fullWidth
                  required
                  helperText={
                    formData.entryType === 'absence'
                      ? (preservedAutoReason
                        ? 'Enter your reason. The automatic no-show note is added when you save.'
                        : 'A reason is required for absence records')
                      : formData.adjustmentReason === 'OTHER'
                        ? 'Comment is required when reason is Other'
                        : 'A note is required when creating or editing time entries'
                  }
                />
                
                {/* Show "Mark as Resolved" checkbox for auto clock-out entries */}
                {editingEntry && isAutoClockOut && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={markAsResolved}
                        onChange={(e) => setMarkAsResolved(e.target.checked)}
                      />
                    }
                    label="Mark as Resolved (will not appear in Missing Punches Report)"
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {submitting ? 'Saving...' : (editingEntry || editingAbsence ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Manual Delete</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="warning">
                Deleting a time record is a high-impact manual adjustment. The adjustment history will be retained permanently for HR review.
              </Alert>
              <FormControl fullWidth required>
                <InputLabel>Adjustment Reason</InputLabel>
                <Select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  label="Adjustment Reason"
                >
                  {MANUAL_ADJUSTMENT_REASON_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Comment"
                value={deleteComment}
                onChange={(e) => setDeleteComment(e.target.value)}
                multiline
                rows={3}
                fullWidth
                required
                helperText="A meaningful comment is always required for deletes"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              Delete Record
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
};

export default TimeRecordsManagementPage;
